import { Box, createStyles, Grid, makeStyles } from '@material-ui/core';
import { AppBar, Button, ExtendedTheme, Heading, Link, TextBlock, UserInterfacePreferences, Radio, Footer } from 'neon-design-system';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { config } from '../../../config';
import { baseUrls } from '../../../utils/baseUrls.utils';
import Container from '../../ui/Container';

const useStyles = makeStyles<ExtendedTheme>(theme => createStyles({
	root: {
		display: 'flex',
		flexDirection: 'column',
		position: 'absolute',
		top: theme.spacing(8),
		bottom: 0,
		left: 0,
		right: 0
	},
	container: {
		position: 'relative'
	},
	inheritLink: {
		fontSize: 'inherit'
	},
	sideLink: {
		position:'absolute',
		right: 16,
		top: 16,
		[theme.breakpoints.down('xs')]: {
			display: 'none'
		}
	},
	manageEmailNotificationsLink: {
		display: 'inline-flex',
		alignItems: 'center',
		...theme.customTypography.footnote,
		fontWeight: 'bold'
	},
	manageEmailNotificationsLinkIcon: {
		width: 14,
		marginLeft: theme.spacing(1.125)
	}
}));

enum UnsubscribeResult {
	notSent, unsubscribed, notUnsubscribed
}

export const UnsubscribePage = () => {
	const { search } = useLocation();
	const params = new URLSearchParams(search);
	const data = params.get('data');
	const classes = useStyles();
	const [reason, setReason] = useState('');
	const [email, setEmail] = useState('');
	const [unsubscribeResult , setUnsubscribeResult ] = useState<UnsubscribeResult>(UnsubscribeResult.notSent);
	const reasons = [
		{value: 'too_frequent', label: 'The emails are too frequent'},
		{value: 'not_relevant', label: 'The emails are not relevant to me'},
		{value: 'dont_remember', label: 'I don’t remember signing up for this'},
		{value: 'other', label: 'Other'}
	];

	const request = (body: any) => fetch(`${config.serverUrl}/unsubscribe`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(body)
	}).then(r => r.json()).catch(e => {});

	useEffect(() => {
		request({data}).then(r => r?.email && setEmail(r.email));
	}, [data]);

	const unsubscribe = () => request({data, reason}).then(r => 'status' in r && setUnsubscribeResult(r.status ? UnsubscribeResult.unsubscribed : UnsubscribeResult.notUnsubscribed));

	return <>
		<AppBar
			id="app-header"
			app="Insights" onSignOut={() => {}}/>
		<Box className={classes.root}>
			<Container>
				<Grid container spacing={4} className={classes.container}>
					<Grid item md={10} xs={12}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Heading styleName="largeTitle">{unsubscribeResult === UnsubscribeResult.notSent ? 'Unsubscribe' : unsubscribeResult === UnsubscribeResult.unsubscribed ? 'You are now unsubscribed.' : 'You are already unsubscribed.'}</Heading>
							</Grid>
							<Grid item xs={12}>
								<TextBlock ink="muted">
									{unsubscribeResult === UnsubscribeResult.notSent
										? 'If you no longer wish to receive any email notifications, unsubscribe below. You can manage notifications for specific channels'
										: 'You can resubscribe for email notifications at any time'
									}
									{' '}
									via
									{' '}
									<Link
										className={classes.inheritLink}
										to={config.basePath + baseUrls.emailNotifications}
										ignoreRouter>Manage email notifications</Link>.
								</TextBlock>
							</Grid>
						</Grid>
					</Grid>
					<Box className={classes.sideLink}>
						<Link
							className={classes.manageEmailNotificationsLink}
							to={config.basePath + baseUrls.emailNotifications}
							ignoreRouter
						>
							Manage email notifications
							<UserInterfacePreferences
								className={classes.manageEmailNotificationsLinkIcon}
							/>
						</Link>
					</Box>
					<Grid item xs={12}>
						{unsubscribeResult === UnsubscribeResult.notSent
							? <Grid container spacing={2}>
								<Grid item xs={12}>
									<Grid container spacing={1}>
										<Grid item xs={12}>
											<TextBlock styleName="largeBody2">{email}</TextBlock>
										</Grid>
										<Grid item xs={12}>
											<TextBlock styleName="footnote" ink="muted">
												If you have not requested to unsubscribe please ignore since the email associated with this page was most likely forwarded to you.
											</TextBlock>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<Grid container spacing={1}>
										<Grid item xs={12}>
											<TextBlock styleName="caption1"><strong>I’m unsubscribing because:</strong></TextBlock>
										</Grid>
										{reasons.map(r => <Grid item xs={12} key={r.value}>
											<Radio value={r.value} checked={reason === r.value} label={r.label} onChange={e => setReason(e.target.value)} />
										</Grid>)}
									</Grid>
								</Grid>
								<Grid item xs={12}><Button colorType="primary" onClick={unsubscribe}>Unsubscribe from all article alerts</Button></Grid>
							</Grid>
							: <Button colorType="primary" onClick={() => window.location.href = config.basePath}>Login to Insights</Button>
						}
					</Grid>
				</Grid>
			</Container>
			<Footer />
		</Box>
	</>;
}
